import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Wallet, TrendingDown, History, Phone, Briefcase, User, Receipt, Loader2 } from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const ROLE_LABELS = {
  driver: "سائق",
  mujahez: "مجهّز",
  staff: "موظف",
  admin: "إداري",
};
const ROLE_COLORS = {
  driver: "#3B82F6",
  mujahez: "#7ED321",
  staff: "#F5A623",
  admin: "#1E3A5F",
};

const TX_LABELS = {
  salary_paid: "راتب مدفوع",
  advance: "سلفة",
  loan_given: "قرض",
  loan_repayment: "تسديد",
};
const TX_COLORS = {
  salary_paid: "#7ED321",
  advance: "#F5A623",
  loan_given: "#E85D45",
  loan_repayment: "#3B82F6",
};

// ===== Employee Form Modal =====
const EmployeeForm = ({ employee, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: employee?.name || "",
    phone: employee?.phone || "",
    role: employee?.role || "staff",
    monthly_salary: employee?.monthly_salary || "",
    notes: employee?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!employee;

  const submit = async () => {
    if (!form.name.trim()) { toast.error("الاسم مطلوب"); return; }
    setBusy(true);
    try {
      const data = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        role: form.role,
        monthly_salary: Number(form.monthly_salary || 0),
        notes: form.notes,
      };
      if (isEdit) {
        await axios.patch(`${API}/admin/employees/${employee.id}`, data);
        toast.success("تم تحديث الموظف");
      } else {
        await axios.post(`${API}/admin/employees`, data);
        toast.success("تم إضافة الموظف");
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="employee-form-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5" dir="rtl">
        <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-[#1E3A5F]/20">
          <h3 className="font-bold text-xl text-[#1E3A5F]">{isEdit ? "تعديل موظف" : "إضافة موظف"}</h3>
          <button onClick={onClose} className="text-[#5C6E64] hover:text-[#E85D45]" data-testid="emp-form-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الاسم <span className="text-[#E85D45]">*</span></label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="emp-name" className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الهاتف</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="emp-phone" dir="ltr" className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] bg-white" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">المنصب</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="emp-role" className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] bg-white">
                <option value="staff">موظف</option>
                <option value="driver">سائق</option>
                <option value="mujahez">مجهّز</option>
                <option value="admin">إداري</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الراتب الشهري (د.ع)</label>
            <input type="number" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })}
              data-testid="emp-salary" dir="ltr" className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] bg-white" />
          </div>
          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">ملاحظات</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
              data-testid="emp-notes" className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] bg-white text-sm resize-none" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={submit} disabled={busy} data-testid="emp-save"
            className="flex-1 bg-[#1E3A5F] text-white font-bold py-2 rounded-full disabled:opacity-50">
            {busy ? "جاري الحفظ..." : isEdit ? "حفظ التعديل" : "إضافة"}
          </button>
          <button onClick={onClose} className="bg-[#F3F6F4] text-[#5C6E64] font-bold py-2 px-4 rounded-full">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ===== Transaction Form Modal =====
const TxForm = ({ employee, onClose, onSaved }) => {
  const [type, setType] = useState("salary_paid");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // pre-fill with monthly_salary on salary_paid
  useEffect(() => {
    if (type === "salary_paid" && !amount && employee.monthly_salary) {
      setAmount(String(employee.monthly_salary));
    }
  }, [type, amount, employee]);

  const submit = async () => {
    if (Number(amount) <= 0) { toast.error("أدخل المبلغ"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/salary-transactions`, {
        employee_id: employee.id, type, amount: Number(amount), note,
      });
      toast.success(`تم تسجيل ${TX_LABELS[type]}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="tx-form-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5" dir="rtl">
        <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-[#1E3A5F]/20">
          <h3 className="font-bold text-xl text-[#1E3A5F]">حركة جديدة — {employee.name}</h3>
          <button onClick={onClose} data-testid="tx-form-close" className="text-[#5C6E64]"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          {Object.entries(TX_LABELS).map(([k, lbl]) => (
            <button key={k} onClick={() => setType(k)} data-testid={`tx-type-${k}`}
              className={`py-2 rounded-xl font-bold text-sm border-2 transition-colors ${type === k ? "text-white" : "bg-white text-[#5C6E64] border-[#E4EBE6]"}`}
              style={type === k ? { background: TX_COLORS[k], borderColor: TX_COLORS[k] } : {}}>
              {lbl}
            </button>
          ))}
        </div>

        <div className="mb-3">
          <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">المبلغ (د.ع) <span className="text-[#E85D45]">*</span></label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
            data-testid="tx-amount" dir="ltr" className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] bg-white text-lg font-bold" />
        </div>
        <div className="mb-3">
          <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">ملاحظة</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            data-testid="tx-note" className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] bg-white text-sm resize-none" />
        </div>

        <div className="flex gap-2">
          <button onClick={submit} disabled={busy} data-testid="tx-save"
            className="flex-1 bg-[#1E3A5F] text-white font-bold py-2 rounded-full disabled:opacity-50">
            {busy ? "جاري الحفظ..." : "حفظ الحركة"}
          </button>
          <button onClick={onClose} className="bg-[#F3F6F4] text-[#5C6E64] font-bold py-2 px-4 rounded-full">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ===== Employee History Modal =====
const EmployeeHistory = ({ employee, onClose, onChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/employees/${employee.id}/transactions`);
      setData(res);
    } finally { setLoading(false); }
  }, [employee.id]);
  useEffect(() => { load(); }, [load]);

  const removeTx = async (id) => {
    if (!(await confirmDialog("حذف هذه الحركة؟ سيؤثر على الرصيد"))) return;
    try {
      await axios.delete(`${API}/admin/salary-transactions/${id}`);
      toast.success("حُذفت الحركة");
      load();
      onChange && onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "خطأ"); }
  };

  if (loading || !data) return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <p className="text-white">جاري التحميل...</p>
    </div>
  );

  const emp = data.employee;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="employee-history-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 my-8" dir="rtl">
        <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-[#1E3A5F]/20">
          <h3 className="font-bold text-xl text-[#1E3A5F]">سجل الحركات — {emp.name}</h3>
          <button onClick={onClose} data-testid="history-close" className="text-[#5C6E64]"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className="bg-[#7ED321] text-white rounded-xl p-2.5">
            <p className="text-[10px] opacity-90">راتب شهري</p>
            <p className="font-black text-base" dir="ltr">{fmt(emp.monthly_salary)}</p>
          </div>
          <div className="bg-[#3B82F6] text-white rounded-xl p-2.5">
            <p className="text-[10px] opacity-90">مدفوع هذا الشهر</p>
            <p className="font-black text-base" dir="ltr">{fmt(emp.paid_this_month)}</p>
          </div>
          <div className={`text-white rounded-xl p-2.5 ${emp.outstanding_loans > 0 ? "bg-[#E85D45]" : "bg-[#7ED321]"}`}>
            <p className="text-[10px] opacity-90">القرض/السلف القائمة</p>
            <p className="font-black text-base" dir="ltr">{fmt(emp.outstanding_loans)}</p>
          </div>
          <div className="bg-[#1E3A5F] text-white rounded-xl p-2.5">
            <p className="text-[10px] opacity-90">عدد الحركات</p>
            <p className="font-black text-base">{emp.tx_count}</p>
          </div>
        </div>

        {data.transactions.length === 0 ? (
          <p className="text-center text-[#5C6E64] py-8">لا توجد حركات</p>
        ) : (
          <div className="border rounded-2xl overflow-hidden max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1E3A5F] text-white sticky top-0">
                <tr>
                  <th className="p-2">التاريخ</th>
                  <th className="p-2">النوع</th>
                  <th className="p-2">المبلغ</th>
                  <th className="p-2">ملاحظة</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t, i) => (
                  <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F0F9FF]"} data-testid={`tx-row-${t.id}`}>
                    <td className="p-2 text-center text-xs" dir="ltr">{new Date(t.created_at).toLocaleDateString("en-GB")}</td>
                    <td className="p-2 text-center">
                      <span className="px-2 py-0.5 rounded-full text-white text-xs font-bold" style={{ background: TX_COLORS[t.type] || "#999" }}>
                        {TX_LABELS[t.type] || t.type}
                      </span>
                    </td>
                    <td className="p-2 text-center font-bold" dir="ltr" style={{ color: TX_COLORS[t.type] }}>{fmt(t.amount)}</td>
                    <td className="p-2 text-xs">{t.note || "—"}</td>
                    <td className="p-2 text-center">
                      <button onClick={() => removeTx(t.id)} data-testid={`tx-del-${t.id}`}
                        className="text-[#E85D45] hover:bg-[#FEF2F2] rounded p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Quick Expense Voucher (سند صرف سريع) =====
// Inline form that posts to /api/admin/expenses with category "أخرى".
// Each voucher deducts immediately from today's profit and capital.
const QuickExpenseVoucher = ({ onSaved }) => {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [todaySpent, setTodaySpent] = useState(0);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const [sumRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/expenses/summary`),
        axios.get(`${API}/admin/expenses`),
      ]);
      setTodaySpent(Number(sumRes.data?.today?.all || 0));
      // Show last 5 staff vouchers (category 'أخرى' with note starting with 'موظف:')
      const staffOnes = (listRes.data || [])
        .filter((x) => (x.note || "").startsWith("موظف:"))
        .slice(0, 5);
      setHistory(staffOnes);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = async () => {
    if (!name.trim()) { toast.error("اكتب اسم الموظف"); return; }
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("أدخل مبلغًا صحيحًا"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/expenses`, {
        category: "أخرى",
        amount: amt,
        note: `موظف: ${name.trim()}${note.trim() ? ` — ${note.trim()}` : ""}`,
      });
      toast.success(`تم تسجيل صرف ${fmt(amt)} د.ع لـ ${name.trim()} — تم خصمه من الربح اليومي`);
      setName(""); setAmount(""); setNote("");
      loadHistory();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!(await confirmDialog("حذف هذا السند؟ سيُعاد المبلغ للربح ورأس المال."))) return;
    try {
      await axios.delete(`${API}/admin/expenses/${id}`);
      toast.success("تم الحذف");
      loadHistory();
      onSaved && onSaved();
    } catch { toast.error("فشل الحذف"); }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-[#F59E0B] shadow-md overflow-hidden" data-testid="quick-expense-voucher">
      <div className="bg-gradient-to-l from-[#E85D45] to-[#F97316] text-white p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5" />
          <h4 className="font-bold text-base">سند صرف سريع — موظف</h4>
        </div>
        <div className="bg-white/20 rounded-full px-2 py-0.5 text-[11px] font-bold">
          مصاريف اليوم: <span dir="ltr">{fmt(todaySpent)}</span> د.ع
        </div>
      </div>
      <div className="p-3 space-y-2">
        <p className="text-[11px] text-[#5C6E64] bg-[#FFF7ED] border border-[#FDBA74] rounded-lg p-2">
          📌 سند صرف سريع لأي موظف. ينقص مباشرة من <b>ربح اليوم</b> ومن <b>رأس المال</b>. <span className="text-[#5C6E64]">(مستقل عن منظومة الموظفين الرسمية الخاصة بـ HR.)</span>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_1fr_auto] gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="اسم الموظف (مثال: محمد)"
            data-testid="quick-exp-name"
            className="px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#E85D45] text-sm"
          />
          <input
            type="number"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="المبلغ (د.ع)"
            dir="ltr"
            data-testid="quick-exp-amount"
            className="px-3 py-2.5 rounded-xl border-2 border-[#E85D45] focus:outline-none text-sm font-bold"
          />
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="ملاحظة (اختياري)"
            data-testid="quick-exp-note"
            className="px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#E85D45] text-sm"
          />
          <button
            onClick={submit}
            disabled={busy}
            data-testid="quick-exp-submit"
            className="bg-[#E85D45] hover:bg-[#d54d35] disabled:bg-[#BAE6FD] text-white font-bold py-2.5 px-4 rounded-xl flex items-center justify-center gap-1 text-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            تسجيل
          </button>
        </div>
        {history.length > 0 && (
          <div className="pt-2 border-t border-[#FEE2E2]">
            <p className="text-[11px] font-bold text-[#5C6E64] mb-1">آخر سندات الصرف للموظفين:</p>
            <div className="space-y-1">
              {history.map((h) => (
                <div key={h.id} data-testid={`quick-exp-row-${h.id}`}
                  className="flex items-center justify-between bg-[#FEF2F2] rounded-lg px-2 py-1.5 text-[11px]">
                  <div className="flex-1 min-w-0 truncate">
                    <span className="font-bold text-[#1E3A5F]">{(h.note || "").replace(/^موظف:\s*/, "")}</span>
                    <span className="text-[#5C6E64] mx-1" dir="ltr">· {new Date(h.created_at).toLocaleDateString("en-GB")}</span>
                  </div>
                  <span className="font-black text-[#E85D45]" dir="ltr">−{fmt(h.amount)}</span>
                  <button onClick={() => remove(h.id)} data-testid={`quick-exp-del-${h.id}`}
                    className="text-[#E85D45] hover:bg-white rounded p-1 mr-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Main Tab =====
const SalariesTab = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [txFor, setTxFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/employees`);
      setEmployees(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeEmp = async (emp) => {
    if (!(await confirmDialog(`حذف الموظف "${emp.name}" وكل حركاته؟`))) return;
    try {
      await axios.delete(`${API}/admin/employees/${emp.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "خطأ"); }
  };

  const totalSalary = employees.reduce((s, e) => s + Number(e.monthly_salary || 0), 0);
  const totalOutstanding = employees.reduce((s, e) => s + Number(e.outstanding_loans || 0), 0);
  const totalPaidThisMonth = employees.reduce((s, e) => s + Number(e.paid_this_month || 0), 0);

  return (
    <div className="space-y-4" data-testid="salaries-tab" dir="rtl">
      {/* Quick expense voucher — independent from official HR salaries */}
      <QuickExpenseVoucher />

      <div className="bg-white rounded-2xl p-4 border-2 border-[#1E3A5F]/20 shadow-md">
        <div className="flex flex-wrap items-center justify-between mb-4 pb-3 border-b-2 border-[#1E3A5F]/20 gap-2">
          <h3 className="font-heading font-bold text-xl text-[#1E3A5F] flex items-center gap-2">
            <Wallet className="w-5 h-5" />رواتب العمال والسلف
          </h3>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="add-employee"
            className="bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold px-4 py-2 rounded-full flex items-center gap-2">
            <Plus className="w-4 h-4" />إضافة موظف
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className="bg-[#1E3A5F] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-80">عدد الموظفين</p>
            <p className="font-black text-xl">{employees.length}</p>
          </div>
          <div className="bg-[#7ED321] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-90">إجمالي الرواتب الشهرية</p>
            <p className="font-black text-xl" dir="ltr">{fmt(totalSalary)}</p>
          </div>
          <div className="bg-[#3B82F6] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-90">مدفوع هذا الشهر</p>
            <p className="font-black text-xl" dir="ltr">{fmt(totalPaidThisMonth)}</p>
          </div>
          <div className={`text-white rounded-xl p-3 ${totalOutstanding > 0 ? "bg-[#E85D45]" : "bg-[#7ED321]"}`}>
            <p className="text-[10px] opacity-90">قروض/سلف قائمة</p>
            <p className="font-black text-xl" dir="ltr">{fmt(totalOutstanding)}</p>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>
        ) : employees.length === 0 ? (
          <div className="text-center text-[#5C6E64] py-12">
            <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>لا يوجد موظفون — اضغط "إضافة موظف"</p>
          </div>
        ) : (
          <div className="space-y-3">
            {employees.map((emp) => (
              <div key={emp.id} className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3" data-testid={`emp-${emp.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                      style={{ background: ROLE_COLORS[emp.role] || "#999" }}>
                      <User className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#111814]">{emp.name}</p>
                      <div className="flex flex-wrap gap-2 items-center text-xs text-[#5C6E64]">
                        <span className="px-2 py-0.5 rounded-full text-white text-[10px] font-bold" style={{ background: ROLE_COLORS[emp.role] || "#999" }}>
                          <Briefcase className="w-3 h-3 inline ml-1" />{ROLE_LABELS[emp.role] || emp.role}
                        </span>
                        {emp.phone && (
                          <a href={`tel:${emp.phone}`} className="flex items-center gap-1" dir="ltr">
                            <Phone className="w-3 h-3" />{emp.phone}
                          </a>
                        )}
                      </div>
                      {emp.notes && <p className="text-xs text-[#5C6E64] mt-1">{emp.notes}</p>}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <div className="bg-[#7ED321]/10 border border-[#7ED321] rounded-lg px-2 py-1">
                      <p className="text-[9px] text-[#5C6E64]">راتب شهري</p>
                      <p className="font-bold text-[#7ED321] text-sm" dir="ltr">{fmt(emp.monthly_salary)} د.ع</p>
                    </div>
                    <div className={`border rounded-lg px-2 py-1 ${emp.outstanding_loans > 0 ? "bg-[#E85D45]/10 border-[#E85D45]" : "bg-[#7ED321]/10 border-[#7ED321]"}`}>
                      <p className="text-[9px] text-[#5C6E64]">سلف/قروض</p>
                      <p className={`font-bold text-sm ${emp.outstanding_loans > 0 ? "text-[#E85D45]" : "text-[#7ED321]"}`} dir="ltr">{fmt(emp.outstanding_loans)} د.ع</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                  <button onClick={() => setTxFor(emp)} data-testid={`emp-pay-${emp.id}`}
                    className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-1.5 px-3 rounded-full text-xs flex items-center gap-1">
                    <Plus className="w-3 h-3" />دفع/سلفة/قرض
                  </button>
                  <button onClick={() => setHistoryFor(emp)} data-testid={`emp-hist-${emp.id}`}
                    className="bg-[#3B82F6] hover:bg-[#2563eb] text-white font-bold py-1.5 px-3 rounded-full text-xs flex items-center gap-1">
                    <History className="w-3 h-3" />السجل
                  </button>
                  <button onClick={() => { setEditing(emp); setShowForm(true); }} data-testid={`emp-edit-${emp.id}`}
                    className="bg-[#F5A623] hover:bg-[#d68f1a] text-white font-bold py-1.5 px-3 rounded-full text-xs flex items-center gap-1">
                    <Edit2 className="w-3 h-3" />تعديل
                  </button>
                  <button onClick={() => removeEmp(emp)} data-testid={`emp-del-${emp.id}`}
                    className="bg-[#E85D45] hover:bg-[#c14a36] text-white font-bold py-1.5 px-3 rounded-full text-xs flex items-center gap-1">
                    <Trash2 className="w-3 h-3" />حذف
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && <EmployeeForm employee={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={load} />}
      {txFor && <TxForm employee={txFor} onClose={() => setTxFor(null)} onSaved={load} />}
      {historyFor && <EmployeeHistory employee={historyFor} onClose={() => setHistoryFor(null)} onChange={load} />}
    </div>
  );
};

export default SalariesTab;
